import { Message } from '@rossum/api-client/shared';
import {
  Stack,
  Tooltip,
  tooltipClasses,
  Typography,
} from '@rossum/ui/material';
import { truncate } from 'lodash';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { MESSAGE_MAX_LENGTH } from '../../constants/values';
import MessageDetailDialog from '../../containers/Sidebar/components/MessageDetailDialog';
import { isMessageDetailVisible } from './helpers';
import HTMLMessage from './HTMLMessage';
import Icon from './Icon';

type Props = {
  active: boolean;
  isSimpleMultivalue?: boolean;
  message: Message;
};

const IconWithTooltip = ({
  active,
  isSimpleMultivalue,
  message: { type, content, id, detail },
}: Props) => {
  const intl = useIntl();
  const [showMessageDetail, setShowMessageDetail] = useState(false);

  const messageDetailVisible = isMessageDetailVisible(detail, type);

  return (
    <Stack width={1} data-cy="icon-with-tooltip">
      <Tooltip
        // using open={showMessageDetail ? false : undefined} resulted in weird behaviour
        // tooltip was closing on mousemove even for open={undefined} :shrug:
        {...(showMessageDetail ? { open: false } : {})}
        placement="right"
        arrow={false}
        slotProps={{
          tooltip: {
            sx: {
              [`&.${tooltipClasses.tooltip}`]: {
                backgroundColor: t => t.palette[type].main,
                color: t => t.palette[type].contrastText,
                fontSize: t => t.typography.pxToRem(14),
                textAlign: 'left',
                px: 2,
                py: 1,
              },
            },
          },
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, -9],
                },
              },
            ],
          },
        }}
        title={
          id ? (
            <Stack spacing={1}>
              <HTMLMessage
                content={truncate(content, {
                  length: MESSAGE_MAX_LENGTH,
                })}
              />
              {messageDetailVisible && (
                <Typography
                  component="p"
                  variant="body2"
                  sx={{
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontWeight: 'inherit',
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    setShowMessageDetail(true);
                  }}
                >
                  {intl.formatMessage({
                    id: 'components.documentValidation.sidebar.exceptionalError.moreDetails',
                  })}
                </Typography>
              )}
            </Stack>
          ) : (
            ''
          )
        }
      >
        <Stack>
          <Icon
            type={type}
            active={active}
            isSimpleMultivalue={isSimpleMultivalue}
          />
        </Stack>
      </Tooltip>
      {messageDetailVisible && (
        <MessageDetailDialog
          open={showMessageDetail}
          messageDetail={detail}
          onClose={() => setShowMessageDetail(false)}
          content={content}
          datapointId={id}
        />
      )}
    </Stack>
  );
};

export default IconWithTooltip;
